import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  ArrowLeft,
  Briefcase,
  Database,
  User,
  Calendar,
  Clock,
  ExternalLink,
  Mail,
  Phone,
} from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { getTenantDetails } from "../apiHelper";
import axios from "axios";

const ShiftOSTrialDetail = () => {
  const { id } = useParams(); // CRM Database ID
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth();
  const [loading, setLoading] = useState(true);
  const [crmData, setCrmData] = useState(null);
  const [shiftosData, setShiftosData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const token = await getAccessTokenSilently();

        console.log("Fetching trial details for ID:", id);

        // 1. Fetch CRM database record
        const crmResponse = await getTenantDetails(token, { accountId: id });
        console.log("CRM Response:", crmResponse);

        // Backend returns data directly, not wrapped in 'tenant'
        setCrmData(crmResponse);

        // 2. Fetch ShiftOS account info (if we have the external account ID)
        if (crmResponse.externalAccountId) {
          try {
            // Note: You'll need to implement this endpoint in ShiftOS API
            const shiftosResponse = await axios.get(
              `http://localhost:3002/api/admin/accounts/${crmResponse.externalAccountId}`,
              {
                headers: {
                  "X-API-Key": process.env.REACT_APP_SHIFTOS_API_KEY || "",
                },
              }
            );
            setShiftosData(shiftosResponse.data);
          } catch (err) {
            console.error("Failed to fetch ShiftOS data:", err);
            // Not a critical error, we can still show CRM data
          }
        }

        setLoading(false);
      } catch (err) {
        console.error("Error fetching trial details:", err);
        console.error("Error details:", err.response?.data || err.message);
        setError(
          `Failed to load trial details: ${
            err.response?.data?.message || err.message
          }`
        );
        setLoading(false);
      }
    };

    fetchData();
  }, [id, getAccessTokenSilently]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-green-50/30 p-6 flex items-center justify-center">
        <div className="text-gray-600">Loading trial details...</div>
      </div>
    );
  }

  if (error || !crmData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-green-50/30 p-6 flex items-center justify-center">
        <div className="text-red-600">{error || "Trial not found"}</div>
      </div>
    );
  }

  const daysUntilExpiry = crmData.trialEndDate
    ? Math.ceil(
        (new Date(crmData.trialEndDate) - new Date()) / (1000 * 60 * 60 * 24)
      )
    : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-green-50/30 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigate("/shiftos-trials")}
            className="rounded-xl"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center shadow-lg shadow-green-500/25">
                <Briefcase className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  {crmData.accountName}
                </h1>
                <p className="text-gray-600">ShiftOS Trial Account</p>
              </div>
            </div>
          </div>
          <Badge
            variant="secondary"
            className={`px-4 py-2 text-sm font-medium ${
              daysUntilExpiry && daysUntilExpiry > 7
                ? "bg-green-100 text-green-800"
                : daysUntilExpiry && daysUntilExpiry > 0
                ? "bg-yellow-100 text-yellow-800"
                : "bg-red-100 text-red-800"
            }`}
          >
            {daysUntilExpiry && daysUntilExpiry > 0
              ? `${daysUntilExpiry} days remaining`
              : "Expired"}
          </Badge>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* CRM Information */}
          <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-xl shadow-gray-200/50 rounded-2xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/25">
                  <Database className="h-5 w-5 text-white" />
                </div>
                CRM Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-600">
                  CRM Database Name
                </label>
                <div className="mt-1 text-gray-900 font-mono text-sm bg-gray-50 px-3 py-2 rounded-lg">
                  {crmData.name}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-600">
                  Contact Person
                </label>
                <div className="mt-1 text-gray-900">
                  {crmData.contactPersonFirstName}{" "}
                  {crmData.contactPersonLastName}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-600 flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  Email
                </label>
                <div className="mt-1 text-gray-900">{crmData.contactEmail}</div>
              </div>

              {crmData.contactPhone && (
                <div>
                  <label className="text-sm font-medium text-gray-600 flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    Phone
                  </label>
                  <div className="mt-1 text-gray-900">
                    {crmData.contactPhone}
                  </div>
                </div>
              )}

              <div>
                <label className="text-sm font-medium text-gray-600 flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Trial End Date
                </label>
                <div className="mt-1 text-gray-900">
                  {crmData.trialEndDate
                    ? new Date(crmData.trialEndDate).toLocaleDateString()
                    : "Not set"}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-600 flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Created
                </label>
                <div className="mt-1 text-gray-900">
                  {new Date(crmData.createdAt).toLocaleDateString()}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ShiftOS Information */}
          <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-xl shadow-gray-200/50 rounded-2xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center shadow-lg shadow-green-500/25">
                  <Briefcase className="h-5 w-5 text-white" />
                </div>
                ShiftOS Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {crmData.externalDatabaseName ? (
                <>
                  <div>
                    <label className="text-sm font-medium text-gray-600">
                      ShiftOS Database
                    </label>
                    <div className="mt-1 text-gray-900 font-mono text-sm bg-gray-50 px-3 py-2 rounded-lg">
                      {crmData.externalDatabaseName}
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-600">
                      ShiftOS Account ID
                    </label>
                    <div className="mt-1 text-gray-900 font-mono text-sm bg-gray-50 px-3 py-2 rounded-lg">
                      {crmData.externalAccountId}
                    </div>
                  </div>

                  <div className="pt-4">
                    <Button
                      onClick={() => {
                        // Open ShiftOS login in new tab
                        window.open(`http://localhost:3000`, "_blank");
                      }}
                      className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 shadow-lg shadow-green-500/25"
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Open ShiftOS Manager
                    </Button>
                  </div>

                  {shiftosData && (
                    <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
                      <div className="text-sm font-medium text-green-900 mb-2">
                        Account Status
                      </div>
                      <div className="text-xs text-green-700">
                        {shiftosData.status || "Active"}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-8">
                  <div className="text-gray-400 mb-2">
                    <Database className="h-12 w-12 mx-auto mb-3" />
                  </div>
                  <p className="text-gray-600 text-sm">
                    ShiftOS database not yet provisioned
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* User Information */}
          <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-xl shadow-gray-200/50 rounded-2xl lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-purple-500/25">
                  <User className="h-5 w-5 text-white" />
                </div>
                Master User Login Credentials
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="text-sm text-blue-900 mb-3">
                  <strong>Login Credentials:</strong>
                </div>
                <div className="space-y-3 text-sm text-blue-800">
                  <div>
                    <span className="font-medium">Email:</span>{" "}
                    <span className="font-mono bg-white px-2 py-1 rounded border border-blue-200">
                      {crmData.contactEmail}
                    </span>
                  </div>
                  {crmData.externalUserPassword && (
                    <div>
                      <span className="font-medium">Password:</span>{" "}
                      <span className="font-mono bg-white px-2 py-1 rounded border border-blue-200 text-red-700 font-semibold">
                        {crmData.externalUserPassword}
                      </span>
                      <div className="mt-1 text-xs text-blue-700">
                        ⚠️ This is a temporary password. Please save it and
                        reset after first login.
                      </div>
                    </div>
                  )}
                  <div>
                    <span className="font-medium">Name:</span>{" "}
                    {crmData.contactPersonFirstName}{" "}
                    {crmData.contactPersonLastName}
                  </div>
                  <div className="mt-3 pt-3 border-t border-blue-200 text-xs">
                    This user was created in the ShiftOS master database and can
                    log in at{" "}
                    <a
                      href="http://localhost:3000"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline font-medium"
                    >
                      http://localhost:3000
                    </a>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ShiftOSTrialDetail;
